import { RecurringItem, HomeworkItem, TutorSession, TutorMessage, Class, LongTermAssignment, DailyChecklist, ChecklistTemplate, Reward, ChecklistStatus, AppNotification } from '@homework-app/shared';
import type { VerificationApiResult, VerificationResponse } from '@homework-app/shared';
import { offlineStorage, SyncQueueItem } from './offlineStorage';
import { getApiBaseUrl } from '../constants';

const API_BASE = getApiBaseUrl();

class ApiService {
  private isOnline: boolean = navigator.onLine;
  private getTokenFn: (() => Promise<string | null>) | null = null;

  constructor() {
    window.addEventListener('online', () => { this.isOnline = true; });
    window.addEventListener('offline', () => { this.isOnline = false; });
  }

  // Set the Clerk getToken function
  setGetTokenFn(fn: () => Promise<string | null>) {
    this.getTokenFn = fn;
  }

  setOnlineStatus(online: boolean) {
    this.isOnline = online;
  }

  private async getHeaders(additionalHeaders: HeadersInit = {}): Promise<HeadersInit> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...additionalHeaders as Record<string, string>,
    };
    if (this.getTokenFn) {
      const token = await this.getTokenFn();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    // Fall back to stored access token (PIN login)
    if (!headers['Authorization']) {
      const accessToken = localStorage.getItem('hw_access_token');
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return headers;
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    if (this.getTokenFn) {
      const token = await this.getTokenFn();
      if (token) return { 'Authorization': `Bearer ${token}` };
    }
    // Fall back to stored access token (PIN login)
    const accessToken = localStorage.getItem('hw_access_token');
    if (accessToken) return { 'Authorization': `Bearer ${accessToken}` };
    return {};
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getHeaders(options.headers as Record<string, string>);
    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers,
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Wrapper for GET operations with offline fallback.
   * Tries server first (if online), caches result, falls back to offline storage.
   */
  private async withOfflineFallback<T>(
    serverFn: () => Promise<T>,
    offlineFn: () => Promise<T>,
    cacheFn?: (data: T) => Promise<void>
  ): Promise<T> {
    if (!this.isOnline) {
      return offlineFn();
    }

    try {
      const data = await serverFn();
      if (cacheFn) await cacheFn(data);
      return data;
    } catch (error) {
      return offlineFn();
    }
  }

  /**
   * Wrapper for mutations with offline sync queue.
   * If offline or server fails, queues the operation for later sync.
   */
  private async withOfflineSync<T>(
    serverFn: () => Promise<T>,
    offlineResult: T,
    syncData: Omit<SyncQueueItem, 'id'>
  ): Promise<T> {
    if (!this.isOnline) {
      await offlineStorage.addToSyncQueue(syncData);
      return offlineResult;
    }

    try {
      return await serverFn();
    } catch (error) {
      await offlineStorage.addToSyncQueue(syncData);
      return offlineResult;
    }
  }

  // Homework operations
  async getHomeworkForDate(dateKey: string): Promise<{ [period: number]: HomeworkItem[] }> {
    const getOfflineData = async () => {
      const offlineData = await offlineStorage.getHomeworkForDate(dateKey);
      if (offlineData) return offlineData;

      // If no offline data, create from recurring homework
      const recurringHomework = await offlineStorage.getRecurringHomework();
      const emptyData: { [period: number]: HomeworkItem[] } = {};

      recurringHomework.forEach(item => {
        if (!emptyData[item.period]) emptyData[item.period] = [];
        emptyData[item.period].push({
          id: `offline-${Date.now()}-${Math.random()}`,
          text: item.text,
          completed: false,
          dateKey,
          period: item.period,
          photos: []
        });
      });

      await offlineStorage.saveHomeworkForDate(dateKey, emptyData);
      return emptyData;
    };

    return this.withOfflineFallback(
      () => this.request<{ [period: number]: HomeworkItem[] }>(`/homework/${dateKey}`),
      getOfflineData,
      (data) => offlineStorage.saveHomeworkForDate(dateKey, data)
    );
  }

  async createHomework(dateKey: string, period: number, text: string = '', subject?: string): Promise<HomeworkItem> {
    const offlineItem: HomeworkItem = {
      id: `offline-${Date.now()}-${Math.random()}`,
      dateKey,
      period,
      subject: subject || null,
      text,
      completed: false,
      photos: []
    };

    const saveToOffline = async (item: HomeworkItem) => {
      const currentData = await offlineStorage.getHomeworkForDate(dateKey) || {};
      if (!currentData[period]) currentData[period] = [];
      currentData[period].push(item);
      await offlineStorage.saveHomeworkForDate(dateKey, currentData);
    };

    if (!this.isOnline) {
      await saveToOffline(offlineItem);
      await offlineStorage.addToSyncQueue({ action: 'create', type: 'homework', data: { dateKey, period, text, subject } });
      return offlineItem;
    }

    try {
      const serverItem = await this.request<HomeworkItem>('/homework', {
        method: 'POST',
        body: JSON.stringify({ dateKey, period, text, subject }),
      });
      await saveToOffline(serverItem);
      return serverItem;
    } catch {
      await saveToOffline(offlineItem);
      await offlineStorage.addToSyncQueue({ action: 'create', type: 'homework', data: { dateKey, period, text, subject } });
      return offlineItem;
    }
  }

  async updateHomework(id: string, text: string, completed: boolean): Promise<void> {
    return this.withOfflineSync(
      () => this.request(`/homework/${id}`, { method: 'PUT', body: JSON.stringify({ text, completed }) }),
      undefined,
      { action: 'update', type: 'homework', data: { id, text, completed } }
    );
  }

  async deleteHomework(id: string): Promise<void> {
    return this.withOfflineSync(
      () => this.request(`/homework/${id}`, { method: 'DELETE' }),
      undefined,
      { action: 'delete', type: 'homework', data: { id } }
    );
  }

  async uploadPhoto(homeworkId: string, file: File): Promise<{ id: string; url: string }> {
    const createOfflinePhoto = async () => {
      const offlinePhotoId = `offline-photo-${Date.now()}-${Math.random()}`;
      await offlineStorage.savePhoto(offlinePhotoId, homeworkId, file);
      await offlineStorage.addToSyncQueue({ action: 'create', type: 'photo', data: { homeworkId, photoId: offlinePhotoId } });
      return { id: offlinePhotoId, url: URL.createObjectURL(file) };
    };

    if (!this.isOnline) return createOfflinePhoto();

    try {
      const formData = new FormData();
      formData.append('photo', file);
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/homework/${homeworkId}/photo`, {
        method: 'POST',
        body: formData,
        headers: authHeaders,
      });
      if (!response.ok) throw new Error('Failed to upload photo');
      return response.json();
    } catch {
      return createOfflinePhoto();
    }
  }

  async getRecurringHomework(): Promise<RecurringItem[]> {
    return this.withOfflineFallback(
      () => this.request<RecurringItem[]>('/recurring-homework'),
      () => offlineStorage.getRecurringHomework(),
      (data) => offlineStorage.saveRecurringHomework(data)
    );
  }

  async updateRecurringHomework(items: RecurringItem[], updateExisting: boolean = false, dateKey?: string): Promise<void> {
    await offlineStorage.saveRecurringHomework(items);

    return this.withOfflineSync(
      () => this.request('/recurring-homework', {
        method: 'PUT',
        body: JSON.stringify({ items, updateExistingItems: updateExisting, dateKey }),
      }),
      undefined,
      { action: 'update', type: 'recurring', data: { items, updateExisting, dateKey } }
    );
  }

  async getClasses(): Promise<Class[]> {
    return this.withOfflineFallback(
      () => this.request<Class[]>('/classes'),
      () => offlineStorage.getClasses(),
      (data) => offlineStorage.saveClasses(data)
    );
  }

  async updateClasses(classes: Class[]): Promise<void> {
    await offlineStorage.saveClasses(classes);

    return this.withOfflineSync(
      () => this.request('/classes', {
        method: 'PUT',
        body: JSON.stringify({ classes }),
      }),
      undefined,
      { action: 'update', type: 'classes', data: { classes } }
    );
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.request('/health');
  }

  // ================== LONG-TERM ASSIGNMENTS API METHODS ==================

  async getAssignments(): Promise<LongTermAssignment[]> {
    return this.request<LongTermAssignment[]>('/assignments');
  }

  async createAssignment(assignment: Omit<LongTermAssignment, 'id' | 'completed' | 'created_at' | 'updated_at'>): Promise<LongTermAssignment> {
    return this.request<LongTermAssignment>('/assignments', {
      method: 'POST',
      body: JSON.stringify(assignment),
    });
  }

  async updateAssignment(id: string, assignment: Partial<LongTermAssignment>): Promise<void> {
    return this.request(`/assignments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(assignment),
    });
  }

  async deleteAssignment(id: string): Promise<void> {
    return this.request(`/assignments/${id}`, {
      method: 'DELETE',
    });
  }

  // ================== TUTOR API METHODS ==================

  // Get all tutor sessions
  async getTutorSessions(): Promise<TutorSession[]> {
    return this.request<TutorSession[]>('/tutor/sessions');
  }

  // Get messages for a session
  async getTutorMessages(sessionId: string): Promise<TutorMessage[]> {
    return this.request<TutorMessage[]>(`/tutor/sessions/${sessionId}/messages`);
  }

  // Create a new tutor session
  async createTutorSession(title?: string): Promise<TutorSession> {
    return this.request<TutorSession>('/tutor/sessions', {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
  }

  // Update a tutor session title
  async updateTutorSessionTitle(sessionId: string, title: string): Promise<{ success: boolean; title: string }> {
    return this.request<{ success: boolean; title: string }>(`/tutor/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  }

  // Send a message to the tutor (returns EventSource for streaming)
  async sendTutorMessage(
    sessionId: string,
    message: string,
    imageFile?: File,
    onChunk?: (text: string) => void,
    onComplete?: () => void,
    onError?: (error: string) => void
  ): Promise<void> {
    const formData = new FormData();
    formData.append('sessionId', sessionId);
    formData.append('message', message);

    if (imageFile) {
      formData.append('image', imageFile);
    }

    try {
      const authHeaders = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/tutor/chat`, {
        method: 'POST',
        body: formData,
        headers: authHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to send message');
      }

      // Handle SSE streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'text' && onChunk) {
                onChunk(data.content);
              } else if (data.type === 'done' && onComplete) {
                onComplete();
              } else if (data.type === 'error' && onError) {
                onError(data.error);
              }
            } catch (e) {
              // Ignore JSON parse errors
            }
          }
        }
      }

      // Process any remaining data in buffer
      if (buffer.startsWith('data: ')) {
        try {
          const data = JSON.parse(buffer.slice(6));
          if (data.type === 'done' && onComplete) {
            onComplete();
          }
        } catch (e) {
          // Ignore
        }
      }
    } catch (error) {
      if (onError) {
        onError(error instanceof Error ? error.message : 'Unknown error');
      }
      throw error;
    }
  }

  // Upload image for tutor (pre-upload)
  async uploadTutorImage(file: File): Promise<{ url: string; filename: string }> {
    const formData = new FormData();
    formData.append('image', file);

    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE}/tutor/upload`, {
      method: 'POST',
      body: formData,
      headers: authHeaders,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to upload image');
    }

    return response.json();
  }

  // Sync operations
  async syncOfflineData(): Promise<void> {
    if (!this.isOnline) {
      throw new Error('Cannot sync while offline');
    }

    const queue = await offlineStorage.getSyncQueue();

    // Track dates that need to be refreshed from server
    const datesToRefresh = new Set<string>();

    // Group queue items by dateKey to process all items for a date together
    const itemsByDate = new Map<string, any[]>();

    for (const item of queue) {
      if (item.type === 'homework' && item.action === 'create' && item.data.dateKey) {
        const dateKey = item.data.dateKey;
        if (!itemsByDate.has(dateKey)) {
          itemsByDate.set(dateKey, []);
        }
        itemsByDate.get(dateKey)!.push(item);
      }
    }

    // Process creates by fetching current state from offline storage
    for (const [dateKey] of itemsByDate.entries()) {
      try {
        // Get the current state from offline storage (includes all updates)
        const offlineData = await offlineStorage.getHomeworkForDate(dateKey);

        if (offlineData) {
          // Find all items that need to be created (have offline IDs)
          for (const period in offlineData) {
            for (const homework of offlineData[period]) {
              if (homework.id?.startsWith('offline-')) {
                // Create with the CURRENT state (not the queued state)
                await this.request('/homework', {
                  method: 'POST',
                  body: JSON.stringify({
                    dateKey,
                    period: parseInt(period),
                    text: homework.text,
                    completed: homework.completed
                  }),
                });
              }
            }
          }
        }

        datesToRefresh.add(dateKey);
      } catch (error) {
        console.error(`Failed to sync items for ${dateKey}:`, error);
      }
    }

    // Process updates and deletes for server-created items
    for (const item of queue) {
      try {
        if (item.type === 'homework') {
          if (item.action === 'update' && !item.data.id?.startsWith('offline-')) {
            await this.request(`/homework/${item.data.id}`, {
              method: 'PUT',
              body: JSON.stringify({
                text: item.data.text,
                completed: item.data.completed
              }),
            });
          } else if (item.action === 'delete' && !item.data.id?.startsWith('offline-')) {
            await this.request(`/homework/${item.data.id}`, {
              method: 'DELETE',
            });
          }
        } else if (item.type === 'recurring' && item.action === 'update') {
          await this.request('/recurring-homework', {
            method: 'PUT',
            body: JSON.stringify({
              items: item.data.items,
              updateExistingItems: item.data.updateExisting,
              dateKey: item.data.dateKey
            }),
          });
        } else if (item.type === 'photo' && item.action === 'create') {
          const blob = await offlineStorage.getPhoto(item.data.photoId);
          if (blob) {
            const formData = new FormData();
            formData.append('photo', blob);

            const syncAuthHeaders = await this.getAuthHeaders();
            await fetch(`${API_BASE}/homework/${item.data.homeworkId}/photo`, {
              method: 'POST',
              body: formData,
              headers: syncAuthHeaders,
            });

            // Delete offline photo after successful sync
            await offlineStorage.deletePhoto(item.data.photoId);
          }
        }

        // Remove from queue after successful sync
        await offlineStorage.removeSyncQueueItem(item.id);
      } catch (error) {
        console.error(`Failed to sync item ${item.id}:`, error);
        // Continue with next item, leave failed item in queue
      }
    }

    // Refresh all affected dates from server to get proper IDs
    for (const dateKey of datesToRefresh) {
      try {
        const serverData = await this.request<{ [period: number]: HomeworkItem[] }>(`/homework/${dateKey}`);
        await offlineStorage.saveHomeworkForDate(dateKey, serverData);
      } catch (error) {
        console.error(`Failed to refresh data for ${dateKey}:`, error);
      }
    }
  }

  // ================== CHECKLIST API METHODS ==================

  async getDailyChecklist(dateKey: string): Promise<DailyChecklist> {
    return this.request<DailyChecklist>(`/checklist/daily/${dateKey}`);
  }

  async updateChecklistStatus(itemId: string, status: ChecklistStatus, parentComment?: string): Promise<void> {
    return this.request(`/checklist/items/${itemId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, parent_comment: parentComment }),
    });
  }

  async uploadChecklistPhoto(itemId: string, file: File, phase: string): Promise<{ id: string; url: string; phase: string }> {
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('phase', phase);

    const authHeaders = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE}/checklist/items/${itemId}/photo`, {
      method: 'POST',
      body: formData,
      headers: authHeaders,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to upload photo');
    }

    return response.json();
  }

  async completeTimer(itemId: string): Promise<void> {
    return this.request(`/checklist/items/${itemId}/complete-timer`, {
      method: 'POST',
    });
  }

  async completeHonor(itemId: string): Promise<void> {
    return this.request(`/checklist/items/${itemId}/honor-complete`, {
      method: 'POST',
    });
  }

  // Alias used by ChecklistView
  async getChecklist(dateKey: string): Promise<DailyChecklist> {
    return this.getDailyChecklist(dateKey);
  }

  async getChecklistTemplates(): Promise<ChecklistTemplate[]> {
    return this.request<ChecklistTemplate[]>('/checklist/templates');
  }

  // Alias used by TemplateConfig
  async getTemplates(): Promise<ChecklistTemplate[]> {
    return this.getChecklistTemplates();
  }

  async createTemplate(data: Record<string, unknown>): Promise<ChecklistTemplate> {
    return this.request<ChecklistTemplate>('/checklist/templates', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateTemplate(id: string, data: Record<string, unknown>): Promise<ChecklistTemplate> {
    return this.request<ChecklistTemplate>(`/checklist/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteTemplate(id: string): Promise<void> {
    await this.request<{ success: boolean }>(`/checklist/templates/${id}`, { method: 'DELETE' });
  }

  async seedTemplates(studentId: string): Promise<ChecklistTemplate[]> {
    return this.request<ChecklistTemplate[]>('/checklist/templates/seed', {
      method: 'POST',
      body: JSON.stringify({ student_id: studentId })
    });
  }

  async approveAll(studentId: string, dateKey: string): Promise<void> {
    await this.request('/checklist/approve-all', {
      method: 'POST',
      body: JSON.stringify({ student_id: studentId, date_key: dateKey })
    });
  }

  // ---- Rewards API ----

  async getRewards(days?: number): Promise<Reward[]> {
    const qs = days ? `?days=${days}` : '';
    return this.request<Reward[]>(`/rewards${qs}`);
  }

  async getStreak(): Promise<{ streak: number }> {
    return this.request<{ streak: number }>('/rewards/streak');
  }

  // ================== VERIFICATION API METHODS ==================

  async getVerificationResults(checklistItemId: string): Promise<VerificationApiResult[]> {
    return this.request<VerificationApiResult[]>(`/verify/${checklistItemId}`);
  }

  async triggerVerification(checklistItemId: string): Promise<VerificationResponse> {
    return this.request<VerificationResponse>('/verify', {
      method: 'POST',
      body: JSON.stringify({ checklist_item_id: checklistItemId }),
    });
  }

  // ---- Notifications API ----

  async getNotifications(unreadOnly: boolean = false): Promise<AppNotification[]> {
    const qs = unreadOnly ? '?unread=true' : '';
    return this.request<AppNotification[]>(`/notifications${qs}`);
  }

  async getNotificationCount(): Promise<{ count: number }> {
    return this.request<{ count: number }>('/notifications/unread-count');
  }

  async markNotificationRead(id: string): Promise<void> {
    await this.request(`/notifications/${id}/read`, { method: 'PATCH' });
  }

  async markAllNotificationsRead(): Promise<void> {
    await this.request('/notifications/read-all', { method: 'POST' });
  }

  async registerPushToken(token: string, deviceName?: string, platform?: string): Promise<void> {
    await this.request('/notifications/push-token', {
      method: 'POST',
      body: JSON.stringify({ token, device_name: deviceName, platform }),
    });
  }

  async deactivatePushToken(token: string): Promise<void> {
    await this.request('/notifications/push-token', {
      method: 'DELETE',
      body: JSON.stringify({ token }),
    });
  }
}

export const apiService = new ApiService();
